// modules/MultiplayerPlayerManager/TeamFollowing.js
// 多人玩家组队跟随模块

import DebugHelper from '../DebugHelper.js';
import { getPlayerAnimKey, getPlayerStaticFrame } from '../PlayerSpriteFrames.js';

export default class TeamFollowing {
    constructor(scene, networkManager, positionSync, playerRenderer) {
        this.scene = scene;
        this.networkManager = networkManager;
        this.positionSync = positionSync;
        this.playerRenderer = playerRenderer;
        
        // 组队跟随相关
        this.teamLeaderId = null; // 队长ID（如果本地玩家是队员，这是队长的ID）
        this.memberOffsets = new Map(); // 每个队员的跟随偏移 {playerId: {x, y}}
        this.isLocalPlayerLeader = false; // 本地玩家是否是队长
        this.leaderDirection = 'down'; // 队长的移动方向
        this.leaderPositionHistory = []; // 队长的位置历史（用于蛇形跟随）
        this.maxHistoryLength = 20; // 最大历史长度
        
        // 本地玩家精灵引用
        this.localPlayerSprite = null;
        this.localVehicleSprite = null;
    }
    
    /**
     * 设置本地玩家引用
     * @param {Phaser.GameObjects.Sprite} localPlayerSprite - 本地玩家精灵
     * @param {Phaser.GameObjects.Sprite} localVehicleSprite - 本地战车精灵
     */
    setLocalPlayer(localPlayerSprite, localVehicleSprite) {
        this.localPlayerSprite = localPlayerSprite;
        this.localVehicleSprite = localVehicleSprite;
    }
    
    /**
     * 计算跟随位置（根据队长位置和方向）
     * @param {Object} leaderPos - 队长位置 {x, y, direction}
     * @param {number} followDistance - 跟随距离
     * @param {Array} positionHistory - 位置历史数组
     * @param {string} currentDirection - 当前方向
     * @returns {Object} 目标位置 {x, y}
     */
    calculateFollowPosition(leaderPos, followDistance, positionHistory = null, currentDirection) {
        let targetX, targetY;
        
        // 使用位置历史实现蛇形跟随
        // 减少历史延迟，从4帧改为2帧，减少滞后感
        if (positionHistory && positionHistory.length > 1) {
            const historyIndex = Math.min(2, positionHistory.length - 1);
            const historyPos = positionHistory[positionHistory.length - 1 - historyIndex];
            if (historyPos) {
                
                // 根据队长的移动方向，队员应该跟随在队长的后方
                // 在2D游戏中，Y轴向下为正
                switch (historyPos.direction) {
            case 'up':
                // 队长向上移动时，队员应该在队长的后方（下方，Y增加）
                targetX = historyPos.x;
                targetY = historyPos.y + followDistance;
                break;
            case 'down':
                // 队长向下移动时，队员应该在队长的后方（上方，Y减少）
                // 注意：这里需要反转，因为队员应该在队长移动方向的相反方向
                targetX = historyPos.x;
                targetY = historyPos.y - followDistance;
                break;
                    case 'left':
                        // 队长向左，队员在右方（跟随在队长后方）
                        targetX = historyPos.x + followDistance;
                        targetY = historyPos.y;
                        break;
                    case 'right':
                        // 队长向右，队员在左方（跟随在队长后方）
                        targetX = historyPos.x - followDistance;
                        targetY = historyPos.y;
                        break;
                    default:
                        targetX = historyPos.x;
                        targetY = historyPos.y + followDistance;
                }
                
                
                return { x: targetX, y: targetY };
            }
        }
        
        // 如果历史不够，使用当前方向
        const direction = leaderPos.direction || currentDirection;
        // 根据队长的移动方向，队员应该跟随在队长的后方
        switch (direction) {
            case 'up':
                // 队长向上移动时，队员应该在队长的后方（下方，Y增加）
                targetX = leaderPos.x;
                targetY = leaderPos.y + followDistance;
                break;
            case 'down':
                // 队长向下移动时，队员应该在队长的后方（上方，Y减少）
                // 注意：这里需要反转，因为队员应该在队长移动方向的相反方向
                targetX = leaderPos.x;
                targetY = leaderPos.y - followDistance;
                break;
            case 'left':
                // 队长向左，队员在右方
                targetX = leaderPos.x + followDistance;
                targetY = leaderPos.y;
                break;
            case 'right':
                // 队长向右，队员在左方
                targetX = leaderPos.x - followDistance;
                targetY = leaderPos.y;
                break;
            default:
                targetX = leaderPos.x;
                targetY = leaderPos.y + followDistance;
        }
        
        
        return { x: targetX, y: targetY };
    }
    
    /**
     * 更新跟随位置（当本地玩家是队员时调用）
     * @param {Phaser.GameObjects.Sprite} localSprite - 本地玩家精灵
     */
    updateFollowerPosition(localSprite) {
        if (this.isLocalPlayerLeader || !this.teamLeaderId) {
            return; // 不是队员，不需要跟随
        }
        
        // 尝试从场景获取本地玩家精灵
        if (!this.localPlayerSprite && localSprite) {
            this.localPlayerSprite = localSprite;
        }
        
        if (!this.localPlayerSprite) {
            return;
        }
        
        // 查找队长（远程玩家）
        const leaderSprite = this.playerRenderer.getPlayerSprite(this.teamLeaderId);
        
        if (leaderSprite) {
            // 使用队长精灵的位置
            this.updateFollowerPositionFromSprite(leaderSprite);
        } else {
            // 如果找不到队长精灵，尝试从网络管理器获取队长位置
            const leaderPlayer = this.networkManager.getPlayer(this.teamLeaderId);
            if (leaderPlayer && leaderPlayer.x !== undefined && leaderPlayer.y !== undefined) {
                // 使用网络管理器中的队长位置
                this.updateFollowerPositionFromData(leaderPlayer);
            }
        }
    }
    
    /**
     * 从队长精灵更新跟随位置
     * @param {Phaser.GameObjects.Sprite} leaderSprite - 队长精灵
     */
    updateFollowerPositionFromSprite(leaderSprite) {
        if (!this.localPlayerSprite || !leaderSprite) {
            return;
        }
        
        // 从精灵获取队长的方向（优先使用精灵的data，否则使用缓存的direction）
        const leaderDirection = leaderSprite.getData('direction') || this.leaderDirection || 'down';
        
        // 更新缓存的队长方向
        if (leaderDirection !== this.leaderDirection) {
            this.leaderDirection = leaderDirection;
        }
        
        // 记录队长位置历史
        this.leaderPositionHistory.push({ 
            x: leaderSprite.x, 
            y: leaderSprite.y, 
            direction: leaderDirection 
        });
        if (this.leaderPositionHistory.length > this.maxHistoryLength) {
            this.leaderPositionHistory.shift();
        }
        
        
        // 根据队长的移动方向，计算队员应该跟随的位置（在队长后方）
        const followDistance = 32; // 跟随距离（贴近队长）
        const targetPos = this.calculateFollowPosition(
            { x: leaderSprite.x, y: leaderSprite.y, direction: leaderDirection },
            followDistance,
            this.leaderPositionHistory,
            leaderDirection
        );
        
        this.applyFollowerPosition(targetPos.x, targetPos.y);
    }
    
    /**
     * 从队长数据更新跟随位置
     * @param {Object} leaderData - 队长数据（包含x, y, direction）
     */
    updateFollowerPositionFromData(leaderData) {
        const followDistance = 32;
        
        // 记录队长位置历史
        if (leaderData.direction) {
            this.leaderDirection = leaderData.direction;
        }
        this.leaderPositionHistory.push({ 
            x: leaderData.x, 
            y: leaderData.y, 
            direction: leaderData.direction || this.leaderDirection 
        });
        if (this.leaderPositionHistory.length > this.maxHistoryLength) {
            this.leaderPositionHistory.shift();
        }
        
        // 使用位置历史实现蛇形跟随
        const targetPos = this.calculateFollowPosition(
            { x: leaderData.x, y: leaderData.y, direction: leaderData.direction },
            followDistance,
            this.leaderPositionHistory,
            this.leaderDirection
        );
        
        
        this.applyFollowerPosition(targetPos.x, targetPos.y);
    }
    
    /**
     * 应用跟随位置
     * @param {number} targetX - 目标X坐标
     * @param {number} targetY - 目标Y坐标
     */
    applyFollowerPosition(targetX, targetY) {
        if (!this.localPlayerSprite) {
            return;
        }
        
        // 平滑移动到目标位置
        const currentX = this.localPlayerSprite.x;
        const currentY = this.localPlayerSprite.y;
        const dx = targetX - currentX;
        const dy = targetY - currentY;
        const distanceSq = dx * dx + dy * dy;
        
        if (distanceSq > 1) {
            // 使用线性插值实现平滑跟随
            // 根据距离动态调整速度：距离越远，速度越快，确保能跟上队长
            const distance = Math.sqrt(distanceSq);
            let lerpSpeed = 0.3; // 基础速度
            
            // 如果距离太远（超过64像素），加快速度
            if (distance > 64) {
                lerpSpeed = 0.6; // 快速跟上
            } else if (distance > 32) {
                lerpSpeed = 0.4; // 中等速度
            }
            
            // 如果距离非常远（超过128像素），直接瞬移到目标位置附近
            if (distance > 128) {
                this.localPlayerSprite.x = targetX;
                this.localPlayerSprite.y = targetY;
            } else {
                this.localPlayerSprite.x = currentX + dx * lerpSpeed;
                this.localPlayerSprite.y = currentY + dy * lerpSpeed;
            }
        } else {
            this.localPlayerSprite.x = targetX;
            this.localPlayerSprite.y = targetY;
        }
        
        // 同步更新战车位置
        if (this.localVehicleSprite) {
            this.localVehicleSprite.x = this.localPlayerSprite.x;
            this.localVehicleSprite.y = this.localPlayerSprite.y;
        }
        
        // 检测移动方向并更新动画
        // 使用目标位置和当前位置的距离来判断是否在移动（更准确）
        const isMoving = distanceSq > 1; // 如果距离目标位置超过1像素，说明在移动
        
        // 检测移动方向（基于目标位置和当前位置的差值）
        let moveDirection = this.leaderDirection;
        if (distanceSq > 1) {
            if (Math.abs(dx) > Math.abs(dy)) {
                moveDirection = dx > 0 ? 'right' : 'left';
            } else if (dy !== 0) {
                moveDirection = dy > 0 ? 'down' : 'up';
            }
        }
        
        // 更新本地玩家的动画状态（使用 WorldPlayer 的动画系统）
        if (this.scene && this.scene.worldPlayer) {
            const worldPlayer = this.scene.worldPlayer;
            // 确保 playerSprite 存在且与 localPlayerSprite 是同一个对象
            if (worldPlayer.playerSprite && worldPlayer.playerSprite === this.localPlayerSprite) {
                // 更新动画状态（每帧都更新，确保动画正确播放）
                worldPlayer.playerAnimations.isMoving = isMoving;
                worldPlayer.playerAnimations.currentDirection = moveDirection;
                // 调用 WorldPlayer 的动画更新方法（使用正确的本地玩家动画键名）
                worldPlayer.updatePlayerAnimations();
            } else {
                // 如果 playerSprite 不存在或不匹配，直接使用精灵的动画系统
                if (this.localPlayerSprite && this.localPlayerSprite.anims) {
                    const element = this.localPlayerSprite.texture?.key || 'player1';
                    if (isMoving) {
                        const targetAnim = getPlayerAnimKey(element, moveDirection);
                        if (targetAnim && this.scene.anims.exists(targetAnim)) {
                            if (!this.localPlayerSprite.anims.isPlaying || this.localPlayerSprite.anims.currentAnim?.key !== targetAnim) {
                                this.localPlayerSprite.play(targetAnim, true);
                            }
                        }
                    } else {
                        this.localPlayerSprite.anims.stop();
                        this.localPlayerSprite.setFrame(getPlayerStaticFrame(element, moveDirection));
                    }
                }
            }
        } else {
            // 如果没有 WorldPlayer，使用远程玩家动画方法作为后备
            const lastDirection = this.positionSync.getDirection(this.networkManager.playerId);
            const lastIsMoving = this.positionSync.getMovingState(this.networkManager.playerId);
            if (moveDirection !== lastDirection || isMoving !== lastIsMoving) {
                if (this.localPlayerSprite.anims) {
                    this.playerRenderer.updateRemotePlayerAnimation(
                        this.localPlayerSprite, 
                        this.networkManager.playerId, 
                        moveDirection, 
                        isMoving
                    );
                }
                this.positionSync.setDirection(this.networkManager.playerId, moveDirection);
                this.positionSync.setMovingState(this.networkManager.playerId, isMoving);
            }
        }
        
        // 保存当前位置
        this.positionSync.setPosition(this.networkManager.playerId, this.localPlayerSprite.x, this.localPlayerSprite.y);
    }
    
    /**
     * 更新队员位置（当本地玩家是队长时调用）
     * @param {Phaser.GameObjects.Sprite} localPlayerSprite - 本地玩家精灵
     * @param {Array} teamMemberIds - 队伍成员ID列表
     */
    updateTeamMemberPositions(localPlayerSprite, teamMemberIds) {
        if (!this.isLocalPlayerLeader) {
            return;
        }
        
        if (!localPlayerSprite) {
            return;
        }
        
        // 更新所有远程队员的位置（蛇形跟随）
        const remotePlayers = this.playerRenderer.remotePlayers;
        if (remotePlayers.size === 0) return;
        
        // 如果没有队伍成员，不执行跟随逻辑
        if (teamMemberIds.length === 0) {
            return;
        }
        
        const followDistance = 32; // 跟随距离（贴近队长）
        
        // 先过滤出队伍中的远程玩家，并按顺序计算索引
        const teamRemotePlayers = [];
        remotePlayers.forEach((sprite, playerId) => {
            // 检查这个玩家是否在队伍中
            if (!teamMemberIds.includes(playerId)) {
                return; // 不在队伍中，跳过
            }
            
            // 检查是否有这个队员的偏移设置
            if (!this.memberOffsets.has(playerId)) {
                return; // 没有偏移设置，跳过
            }
            
            teamRemotePlayers.push({ sprite, playerId });
        });
        
        // 遍历队伍中的远程玩家，按顺序计算跟随位置
        teamRemotePlayers.forEach(({ sprite, playerId }, memberIndex) => {
            // 获取上一帧位置（用于动画检测）
            const lastPosForMove = this.positionSync.getLastPosition(playerId) || { x: sprite.x, y: sprite.y };
            
            // 计算队员应该跟随的位置（基于队长的移动方向）
            let targetX, targetY;
            let targetDirection = this.leaderDirection || 'down';
            
            // 使用位置历史实现更平滑的蛇形跟随
            let historyPos = null;
            if (this.leaderPositionHistory.length > 0) {
                // 根据队员索引，从历史中选择对应的位置
                // 减少历史延迟：从每个队员间隔3个改为间隔1-2个，减少滞后感
                const historyIndex = Math.min(
                    (memberIndex + 1) * 2, // 每个队员间隔2个历史位置（减少滞后）
                    this.leaderPositionHistory.length - 1
                );
                historyPos = this.leaderPositionHistory[this.leaderPositionHistory.length - 1 - historyIndex];
            }
            
            // 计算跟随位置（考虑队员索引的偏移）
            // memberIndex 从 0 开始，第一个队员跟在队长后面，第二个队员跟在第一个队员后面，以此类推
            const memberFollowDistance = followDistance * (memberIndex + 1);
            if (historyPos) {
                targetDirection = historyPos.direction || this.leaderDirection;
                // 直接计算该队员应该跟随的位置（基于历史位置和队员索引）
                const targetPos = this.calculateFollowPosition(
                    { x: historyPos.x, y: historyPos.y, direction: targetDirection },
                    memberFollowDistance,
                    null,
                    targetDirection
                );
                targetX = targetPos.x;
                targetY = targetPos.y;
            } else {
                // 如果历史不够，使用当前方向
                targetDirection = this.leaderDirection;
                const targetPos = this.calculateFollowPosition(
                    { x: localPlayerSprite.x, y: localPlayerSprite.y, direction: targetDirection },
                    memberFollowDistance,
                    null,
                    targetDirection
                );
                targetX = targetPos.x;
                targetY = targetPos.y;
            }
            
            // 平滑移动到目标位置
            const currentX = sprite.x;
            const currentY = sprite.y;
            const dx = targetX - currentX;
            const dy = targetY - currentY;
            const distanceSq = dx * dx + dy * dy;
            
            
            // 检测移动方向（用于动画）
            let moveDirection = targetDirection;
            if (distanceSq > 1) {
                if (Math.abs(dx) > Math.abs(dy)) {
                    moveDirection = dx > 0 ? 'right' : 'left';
                } else if (dy !== 0) {
                    moveDirection = dy > 0 ? 'down' : 'up';
                }
                
                // 使用线性插值实现平滑跟随
                // 根据距离动态调整速度：距离越远，速度越快，确保能跟上队长
                const distance = Math.sqrt(distanceSq);
                let lerpSpeed = 0.3; // 基础速度
                
                // 如果距离太远（超过64像素），加快速度
                if (distance > 64) {
                    lerpSpeed = 0.6; // 快速跟上
                } else if (distance > 32) {
                    lerpSpeed = 0.4; // 中等速度
                }
                
                // 如果距离非常远（超过128像素），直接瞬移到目标位置附近
                if (distance > 128) {
                    sprite.x = targetX;
                    sprite.y = targetY;
                } else {
                    sprite.x = currentX + dx * lerpSpeed;
                    sprite.y = currentY + dy * lerpSpeed;
                }
            } else {
                sprite.x = targetX;
                sprite.y = targetY;
            }
            
            // 检测是否真的在移动（通过比较当前位置和上一帧位置）
            const lastPosForAnimation = this.positionSync.getLastPosition(playerId) || { x: currentX, y: currentY };
            const actualDx = sprite.x - lastPosForAnimation.x;
            const actualDy = sprite.y - lastPosForAnimation.y;
            const actualDistanceSq = actualDx * actualDx + actualDy * actualDy;
            const isMoving = actualDistanceSq > 0.5; // 只有移动超过0.5像素才认为在移动
            
            // 更新动画（只在方向改变或移动状态改变时更新）
            const lastDirection = this.positionSync.getDirection(playerId);
            const lastIsMoving = this.positionSync.getMovingState(playerId);
            
            if (moveDirection !== lastDirection || isMoving !== lastIsMoving) {
                this.playerRenderer.updateRemotePlayerAnimation(sprite, playerId, moveDirection, isMoving);
                this.positionSync.setDirection(playerId, moveDirection);
                this.positionSync.setMovingState(playerId, isMoving);
            }
            
            // 保存当前位置
            this.positionSync.setPosition(playerId, sprite.x, sprite.y);
            
            // 更新名称标签
            this.playerRenderer.updateNameTagPosition(playerId, sprite.x, sprite.y);
        });
    }
    
    /**
     * 记录队长位置历史（当本地玩家是队长时调用）
     * @param {number} x - X坐标
     * @param {number} y - Y坐标
     * @param {string} direction - 方向
     */
    recordLeaderPosition(x, y, direction) {
        if (this.isLocalPlayerLeader) {
            this.leaderDirection = direction;
            // 记录位置历史
            this.leaderPositionHistory.push({ x, y, direction });
            // 限制历史长度
            if (this.leaderPositionHistory.length > this.maxHistoryLength) {
                this.leaderPositionHistory.shift();
            }
        }
    }
    
    /**
     * 设置队伍队长（用于跟随功能）
     * @param {string} leaderId - 队长ID
     * @param {Array} teamMembers - 队伍成员列表
     */
    setTeamLeader(leaderId, teamMembers = []) {
        this.teamLeaderId = leaderId;
        this.isLocalPlayerLeader = false;
        this.memberOffsets.clear();
        
        // 为本地玩家设置跟随偏移（相对于队长）
        let memberIndex = 0;
        teamMembers.forEach(memberId => {
            if (memberId === this.networkManager.playerId) {
                // 这是本地玩家，设置跟随偏移
                const angle = (memberIndex * 60) * Math.PI / 180;
                const radius = 50;
                const offset = {
                    x: Math.cos(angle) * radius,
                    y: Math.sin(angle) * radius
                };
                this.memberOffsets.set(this.networkManager.playerId, offset);
            }
            memberIndex++;
        });
    }
    
    /**
     * 设置本地玩家为队长
     * @param {Array} teamMembers - 队伍成员列表
     */
    setLocalPlayerAsLeader(teamMembers = []) {
        this.teamLeaderId = null;
        this.isLocalPlayerLeader = true;
        this.memberOffsets.clear();
        
        // 为每个队员设置跟随偏移
        let memberIndex = 0;
        teamMembers.forEach(memberId => {
            if (memberId !== this.networkManager.playerId) {
                const angle = (memberIndex * 60) * Math.PI / 180;
                const radius = 50;
                const offset = {
                    x: Math.cos(angle) * radius,
                    y: Math.sin(angle) * radius
                };
                this.memberOffsets.set(memberId, offset);
                memberIndex++;
            }
        });
    }
    
    /**
     * 清除队伍状态
     */
    clearTeam() {
        this.teamLeaderId = null;
        this.isLocalPlayerLeader = false;
        this.memberOffsets.clear();
        this.leaderPositionHistory = [];
    }
}

